/**
 * DataTableBlock - Interactive data table with sorting, filtering, export
 */

import React from 'react'
import { DataTableBlock as DataTableBlockType } from '../../../../lib/cockpit/schemas'
import TableRenderer from '../../renderers/TableRenderer'

interface DataTableBlockProps {
  data: DataTableBlockType['data']
  blockId: string
  compact?: boolean
}

function DataTableBlock({ data, blockId, compact }: DataTableBlockProps) {
  const tableData = {
    columns: data.columns || [],
    rows: data.rows || [],
  }

  const handleExport = () => {
    if (!data.rows || data.rows.length === 0) return
    
    // Simple CSV export
    const columns = data.columns || Object.keys(data.rows[0]).map(k => ({ key: k, label: k }))
    const headers = columns.map((c: any) => c.label).join(',')
    const rows = data.rows.map((row: any) =>
      columns.map((c: any) => JSON.stringify(row[c.key] ?? '')).join(',')
    ).join('\n')
    
    const csv = `${headers}\n${rows}`
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `data-${blockId}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-2">
      {/* Export button */}
      {data.enable_export && !compact && (
        <div className="flex justify-end">
          <button
            onClick={handleExport}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export CSV
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <TableRenderer data={tableData} compact={compact} />
      </div>

      {/* Evidence count */}
      {data.evidence_anchors && data.evidence_anchors.length > 0 && !compact && (
        <div className="text-xs text-gray-500 dark:text-gray-500 flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          {data.evidence_anchors.length} source{data.evidence_anchors.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  )
}

export default DataTableBlock



